import { useTranslation } from 'react-i18next';

import { Badge } from '../../components/primitives.tsx';
import { useChartPresence } from '../../hooks/useChartPresence.ts';

/**
 * "N other people are viewing this chart" — the minimal first UI for `BACKLOG.md` C11's
 * chart-concurrency awareness. Renders nothing when nobody else is on the chart or the stream has
 * not yet reported anyone, matching this component's own "quiet unless there's something to say"
 * register — same reasoning `HelpLink` (`invariants.md`'s help-system section) gives for staying
 * visually understated rather than fighting for attention.
 *
 * De-duplicates by `userId`, not `connectionId`: the same clinician open in two tabs is one person
 * on the chart, not two, for the purpose of this count — the server intentionally does not do this
 * de-duplication itself (`presenceRegistry.ts`'s own header), since a server-side count would lose
 * the ability to tell "the same person, two tabs" apart from "two different people" at all.
 */
export function ChartPresenceIndicator({ patientId }: { readonly patientId: string }) {
  const { t } = useTranslation('patientChart');
  const { viewers } = useChartPresence(patientId);

  const otherViewers = new Set(viewers.map((viewer) => viewer.userId));
  if (otherViewers.size === 0) return null;

  return <Badge tone="accent">{t('presence.viewing', { count: otherViewers.size })}</Badge>;
}
